import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Users, Trash2, CheckCircle2, Clock, MailCheck, Loader2 } from 'lucide-react';
import { Button } from './ui/button';

import { getBackendUrl } from '../utils/getBackendUrl';
const BACKEND_URL = getBackendUrl();
const API = `${BACKEND_URL}/api`;

const STATUS_CONFIG = {
  awaiting_confirmation: { label: 'Pending client confirmation', className: 'bg-amber-100 text-amber-700 border-amber-300', Icon: Clock },
  invited: { label: 'Active — awaiting password setup', className: 'bg-blue-100 text-blue-700 border-blue-300', Icon: MailCheck },
};

export const AdminFamilyInvites = () => {
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  const fetchInvites = async () => {
    try {
      const r = await axios.get(`${API}/family/all-invites`);
      setInvites(r.data);
    } catch (e) {
      toast.error('Failed to load family invites');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInvites();
  }, []);

  const handleApprove = async (invite) => {
    setBusyId(invite.id);
    try {
      await axios.post(`${API}/family/invites/${invite.id}/approve`);
      toast.success(`${invite.invitee_name} approved — setup email sent`);
      fetchInvites();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to approve invite');
    } finally {
      setBusyId(null);
    }
  };

  const handleDelete = async (invite) => {
    setBusyId(invite.id);
    try {
      await axios.delete(`${API}/family/invites/${invite.id}`);
      toast.success('Invite removed');
      fetchInvites();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete invite');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="glass-card p-6" data-testid="admin-family-invites-card">
      <div className="flex items-center gap-3 mb-4">
        <div className="p-3 bg-purple-500/20 rounded-lg">
          <Users className="w-6 h-6 text-purple-400" />
        </div>
        <div>
          <h2 className="text-2xl font-bold text-white">Family & Friends Invites</h2>
          <p className="text-slate-400 text-sm">Members clients have added to their accounts — approve or remove</p>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-6">
          <Loader2 className="w-6 h-6 animate-spin text-purple-400" />
        </div>
      ) : invites.length === 0 ? (
        <p className="text-slate-400 text-sm py-4 text-center" data-testid="no-family-invites-message">
          No family invites yet.
        </p>
      ) : (
        <div className="space-y-2">
          {invites.map((invite) => {
            const cfg = STATUS_CONFIG[invite.status] || STATUS_CONFIG.invited;
            const StatusIcon = cfg.Icon;
            const isPending = invite.status === 'awaiting_confirmation';
            return (
              <div
                key={invite.id}
                className="flex flex-col sm:flex-row sm:items-center gap-2 p-3 rounded-lg border border-gray-200 bg-gray-50"
                data-testid={`admin-family-invite-${invite.id}`}
              >
                <div className="min-w-0 flex-1">
                  <p className="text-gray-900 text-sm font-medium">
                    {invite.invitee_name}
                    <span className="text-gray-500 font-normal"> — {invite.invitee_email}</span>
                  </p>
                  <p className="text-xs text-gray-500">
                    Added by <strong>{invite.client_name}</strong> ({invite.client_email})
                  </p>
                  <span className={`inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 mt-1 rounded-full border ${cfg.className}`}>
                    <StatusIcon className="w-3 h-3" />
                    {cfg.label}
                  </span>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  {isPending && (
                    <Button
                      size="sm"
                      onClick={() => handleApprove(invite)}
                      disabled={busyId === invite.id}
                      className="bg-green-600 hover:bg-green-700 h-9 px-3"
                      data-testid={`approve-family-invite-${invite.id}`}
                    >
                      <CheckCircle2 className="w-4 h-4 mr-1" />
                      Approve
                    </Button>
                  )}
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleDelete(invite)}
                    disabled={busyId === invite.id}
                    className="border-red-400 text-red-500 hover:bg-red-500/10 h-9 px-3"
                    data-testid={`delete-family-invite-${invite.id}`}
                  >
                    <Trash2 className="w-4 h-4 mr-1" />
                    Delete
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default AdminFamilyInvites;
